import { memo } from 'react'
import { Handle, Position, type NodeProps, type Node } from '@xyflow/react'
import { formatCurrency } from '@/lib/constants'
import { Badge } from '@/components/ui/badge'

type CustomNodeData = {
  name: string
  email: string
  referralCount: number
  totalEarnings: number
  status: string
  color: string
}

type CustomNode = Node<CustomNodeData>

export const CustomNetworkNode = memo(({ data }: NodeProps<CustomNode>) => {
  const initials = data.name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)

  return (
    <div className="relative group">
      <Handle type="target" position={Position.Top} className="!bg-primary" />

      <div
        className="px-4 py-3 rounded-lg shadow-lg border-2 bg-background transition-all duration-200 hover:shadow-xl hover:scale-105 cursor-pointer min-w-[180px]"
        style={{ borderColor: data.color }}
      >
        <div className="flex items-center gap-3">
          <div
            className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-sm shadow-md"
            style={{ backgroundColor: data.color }}
          >
            {initials}
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-sm truncate">{data.name}</div>
            <div className="text-xs text-muted-foreground truncate">{data.email}</div>
          </div>
        </div>

        <div className="mt-3 flex items-center justify-between gap-2">
          <Badge variant="secondary" className="text-xs">
            {data.referralCount} {data.referralCount === 1 ? 'indicação' : 'indicações'}
          </Badge>
          <span className="text-xs font-medium text-muted-foreground">
            {formatCurrency(data.totalEarnings)}
          </span>
        </div>
      </div>

      {/* Tooltip on hover */}
      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-popover text-popover-foreground text-xs rounded-md shadow-lg border opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-50">
        <div className="space-y-1">
          <div>
            <span className="font-semibold">{data.name}</span>
          </div>
          <div className="text-muted-foreground">{data.email}</div>
          <div className="pt-1 border-t">
            <div>Indicações: {data.referralCount}</div>
            <div>Total ganho: {formatCurrency(data.totalEarnings)}</div>
            <div>Status: {data.status}</div>
          </div>
        </div>
      </div>

      <Handle type="source" position={Position.Bottom} className="!bg-primary" />
    </div>
  )
})

CustomNetworkNode.displayName = 'CustomNetworkNode'
